/**
 * Sandbox-Sitzung: AWARE durchklicken, ohne sich bei einem Kunden anzumelden.
 *
 * Ablauf: Im GA-Portal auf „Sandbox öffnen" – das Portal holt sich dort je
 * Rolle ein serverseitig signiertes Token (`/api/ga?action=sandbox-open`) und
 * öffnet diese App mit den Tokens im URL-Fragment. Das Fragment wird sofort
 * nach dem Einlesen aus der Adresszeile entfernt: es soll nicht im Verlauf,
 * in Lesezeichen oder in einem geteilten Link landen. An den Server geht ein
 * Fragment ohnehin nie – deshalb kommen die Tokens auf diesem Weg und nicht
 * als Query-Parameter.
 *
 * Die Rechte stecken im jeweiligen Token, nicht in dieser Datei: Für jede Rolle
 * gibt es ein eigenes, einzeln signiertes Token, und der Server leitet die
 * Identität allein aus der Rolle im Token ab (siehe `api/_sandbox.mjs`).
 * Rollenwechsel heißt also nur: ein anderes fertiges Token mitschicken – hier
 * lassen sich keine Rechte erfinden.
 *
 * Bewusst `sessionStorage`: Die Sandbox gilt für diesen Tab. Ein zweiter Tab mit
 * einem echten Kundenzugang bleibt davon unberührt, und Tab zu heißt Schluss.
 */

import { clearMemberSession } from "./memberSession";

// Die vier Rollen plus die Charaktere aus `api/_sandbox.mjs` (Schlüssel wie
// "miriam"). Welche es gibt, bestimmt allein der Server – hier zählt nur die Form.
export type SandboxRole = string;

export type SandboxSession = {
  tokens: Record<SandboxRole, string>;
  role: SandboxRole;
};

const KEY = "sherloxaware_sandbox_session_v1";
const FRAGMENT_KEY = "sandbox";

// Reihenfolge im Balken: erst die vier Rollen, dann die Charaktere. Früher
// stand hier eine feste Liste, und jede fehlende Rolle wurde beim Einlesen still
// verworfen (bis 2026-09-23 öffnete „Als Sammelaccount öffnen" als Administrator).
const ROLE_ORDER: SandboxRole[] = ["admin", "manager", "employee", "shared"];
const ROLE_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;

function isRole(value: unknown): value is SandboxRole {
  return typeof value === "string" && ROLE_PATTERN.test(value);
}

type RoleClaims = { name?: string; label?: string; exp?: number };

function claimsOf(token: string | undefined | null): RoleClaims {
  if (!token) return {};
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as RoleClaims;
  } catch {
    return {};
  }
}

/** Anzeigename einer Rolle aus ihrem Token – reine Anzeige, der Server liest ihn nicht. */
export function sandboxRoleLabel(role: SandboxRole): string {
  const claims = claimsOf(getSandboxSession()?.tokens[role]);
  if (!ROLE_ORDER.includes(role) && claims.name) return `${claims.name} · ${claims.label ?? role}`;
  return claims.label ?? role;
}

export function sandboxDisplayName(): string {
  return claimsOf(sandboxToken()).name ?? "";
}

let cached: SandboxSession | null | undefined;

function read(): SandboxSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SandboxSession>;
    const tokens = parsed.tokens && typeof parsed.tokens === "object" ? parsed.tokens as Record<SandboxRole, string> : null;
    if (!tokens) return null;
    const role = isRole(parsed.role) ? parsed.role : "admin";
    if (typeof tokens[role] !== "string") return null;
    return { tokens, role };
  } catch {
    return null;
  }
}

function write(session: SandboxSession | null): void {
  cached = session;
  try {
    if (session) sessionStorage.setItem(KEY, JSON.stringify(session));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage gesperrt (privates Fenster o.Ä.) – dann gilt die Sitzung nur für
    // diesen Seitenaufruf. Das ist unschön, aber nicht gefährlich.
  }
}

function decodeBundle(raw: string): SandboxSession | null {
  try {
    const json = atob(raw.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = JSON.parse(json) as { tokens?: Record<string, unknown> };
    const tokens: Record<SandboxRole, string> = {};
    for (const [role, value] of Object.entries(parsed.tokens ?? {})) {
      if (isRole(role) && typeof value === "string" && value) tokens[role] = value;
    }
    // Das GA-Portal stellt die gewählte Rolle an den Anfang des Pakets – mit
    // ihr startet die Sitzung, nicht mit der ersten aus ROLE_ORDER.
    const first = Object.keys(parsed.tokens ?? {}).find((key): key is SandboxRole => isRole(key) && Boolean(tokens[key]));
    if (!first) return null;
    return { tokens, role: first };
  } catch {
    return null;
  }
}

/**
 * Beim Start aufrufen (vor dem Rendern): Liegt ein Token-Paket im Fragment,
 * wird es übernommen und das Fragment entfernt.
 *
 * @returns true, wenn gerade eine Sandbox-Sitzung begonnen hat.
 */
export function bootstrapSandboxFromUrl(): boolean {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return false;
  const params = new URLSearchParams(hash);
  const bundle = params.get(FRAGMENT_KEY);
  if (!bundle) return false;

  const session = decodeBundle(bundle);
  // Fragment in jedem Fall aus der Adresszeile nehmen – auch wenn es kaputt
  // war, hat es in der URL nichts verloren.
  params.delete(FRAGMENT_KEY);
  const rest = params.toString();
  try {
    window.history.replaceState(null, "", window.location.pathname + window.location.search + (rest ? `#${rest}` : ""));
  } catch {
    // ignore
  }
  if (!session) return false;
  write(session);
  return true;
}

export function getSandboxSession(): SandboxSession | null {
  if (cached === undefined) cached = read();
  return cached;
}

export function isSandboxActive(): boolean {
  return getSandboxSession() !== null;
}

/** Das Token der gerade aktiven Rolle – das, was `authFetch` mitschickt. */
export function sandboxToken(): string | null {
  const session = getSandboxSession();
  if (!session) return null;
  return session.tokens[session.role] ?? null;
}

export function sandboxRole(): SandboxRole | null {
  return getSandboxSession()?.role ?? null;
}

export function availableSandboxRoles(): SandboxRole[] {
  const session = getSandboxSession();
  if (!session) return [];
  const keys = Object.keys(session.tokens).filter((role) => isRole(role) && Boolean(session.tokens[role]));
  return [...ROLE_ORDER.filter((role) => keys.includes(role)), ...keys.filter((role) => !ROLE_ORDER.includes(role))];
}

/**
 * Rolle wechseln. Danach lädt die Seite neu: Rolle und Route hängen an vielen
 * Stellen zusammen (Admin-Routen werden nur für Admins überhaupt registriert),
 * ein sauberer Neustart ist hier ehrlicher als ein Dutzend Sonderfälle.
 */
export function setSandboxRole(role: SandboxRole, path?: string): void {
  const session = getSandboxSession();
  if (!session || !session.tokens[role]) return;
  write({ ...session, role });
  // Eine Nummern-Anmeldung am Sammelaccount gehört zu dessen Rolle und darf
  // nicht in die nächste mitreisen.
  clearMemberSession();
  try {
    sessionStorage.removeItem("sherloxaware_last_path");
    window.location.assign(path ?? (role === "admin" ? "/admin" : "/dashboard"));
  } catch {
    window.location.reload();
  }
}

export function clearSandboxSession(): void {
  write(null);
  clearMemberSession();
}

/** Ablaufzeitpunkt des aktiven Tokens (ms) – für die Restlaufzeit im Banner. */
export function sandboxExpiresAt(): number | null {
  const token = sandboxToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * HTML einer abgelegten Sandbox-Mail für die Vorschau im iframe: Links öffnen
 * in einem neuen Tab (im sandboxed iframe täten sie sonst nichts), und das
 * Logo, das im Echtversand als Inline-Anhang mitreist, kommt aus dem Frontend.
 */
export function sandboxMailPreview(html: string): string {
  return `<base target="_blank">${html.split("cid:aware-logo").join(`${window.location.origin}/brand/aware-logo-mail.png`)}`;
}
